import PropTypes from "prop-types";
import { Suspense, useEffect, useRef, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { Environment, ArcballControls, Loader } from "@react-three/drei";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Model, isMeshOnlyModel } from "./model";
import { cameraPose, captureThumbnail } from "./model-thumbnail";
import {
  CheckIcon,
  DeleteIcon,
  PaletteIcon,
  WireframeIcon,
} from "./viewer-icons";
import { AutoMeasureStatus, MeasureButton } from "./measurement";
import { MeasurementPanel } from "./measurement-panel";
import { ModelErrorBoundary, ModelLoadError } from "./model-error";

const BACKGROUND = "#ffffff";
// how far the camera starts from the (centered) model.
const CAMERA_DISTANCE = 10;
// the measured (cleaned) mesh is first shown turned this much to the user's
// left (yaw) and tilted forward (pitch), in degrees, for a better view of the
// foot's surface than straight on.
const MEASURED_VIEW_YAW = 30;
const MEASURED_VIEW_PITCH = 30;

// Fullscreen viewer for a processed model.
// 'scanType' - the scanned body part ("Shoe", "Torso", ...), passed on to the
// automatic size measurement.
// 'useMetrics' - whether the body part offers that measurement: "disabled",
// "enabled" (a button runs it on request) or "auto" (it runs by itself on
// preview).
// 'metricsInput' - what that measurement needs about the viewed model —
// { relatedLabId, webOrderId, webOrderUserName, dataUrl, dataFilename }.
// 'measurement' - the saved result of an earlier measurement of this model
// ({ complete, sides, ... }, see useMeasurement()).
// 'onReload' - re-issue `url` (its presigned URL may have expired) — retrying a
// failed download calls this instead of re-fetching the same link.
// 'thumbnail' - capture a snapshot of the model on save and pass it to onSave
// (multiple-scan mode, for the dashboard grid).
export const ModelViewer = ({
  url,
  title,
  scanType,
  useMetrics,
  metricsInput,
  measurement,
  onMeasured,
  onDelete,
  onSave,
  onReload,
  saveConfirm = true,
  thumbnail = false,
}) => {
  const [renderMode, setRenderMode] = useState(
    measurement ? "textureOff" : "textureOn",
  );
  // the result of the measurement — { jobId, modelUrl, complete, sides }.
  // Once it is in, the measured (cleaned) mesh is shown instead of the scanned
  // model, and its sizes stay on screen. A scan that was measured on an earlier
  // visit arrives already measured: `url` is then the measured mesh itself, so
  // the saved mesh URL is not used.
  const [measured, setMeasured] = useState(() =>
    measurement ? { ...measurement, modelUrl: null } : null,
  );
  // the measured feet (one or two) whose sizes are on screen: none when the
  // scan was incomplete (the measurement then did not get them all), only
  // its cleaned mesh.
  const shownSides = measured && measured.complete ? measured.sides : [];
  // an incomplete scan cannot be saved: it is to be removed and taken again.
  const incomplete = Boolean(measured) && !measured.complete;
  // the holes that made it incomplete, outlined on the mesh to show the user
  // what is missing.
  const shownHoles = incomplete ? measured.holes : undefined;
  // once measured, the title tells how the measurement went instead.
  const shownTitle = !measured
    ? title
    : incomplete
      ? "Incomplete Scan"
      : "Measurement Results";

  // the model failed to download, and the attempt it failed on: the canvas is
  // remounted (or `url` re-issued) to try again.
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // the three.js state ({ gl, scene, camera, initialPose }) for the
  // save-time thumbnail — set once the model's starting view is applied.
  const threeRef = useRef(null);
  // the ArcballControls instance, to reset the view with a new model.
  const controlsRef = useRef(null);
  // the measurement just found the scan incomplete — warn the user once.
  const [incompleteOpen, setIncompleteOpen] = useState(false);
  // the URL of the model on screen once it is fully loaded and in view.
  const [loadedUrl, setLoadedUrl] = useState(null);
  // the automatic measurement is running: neither saving nor discarding the
  // scan meanwhile, as either would lose its results.
  const [measuring, setMeasuring] = useState(false);

  // the measured mesh carries no texture — it is always shown in the flat
  // "Color Off" color, so select that mode with it. It is also a different
  // model, so drop the rotation and panning of the one it replaces.
  function showMeasured(result) {
    setMeasured(result);
    setRenderMode((mode) => (mode === "textureOn" ? "textureOff" : mode));
    if (controlsRef.current) controlsRef.current.reset();
    if (!result.complete) setIncompleteOpen(true);
    if (onMeasured) onMeasured(result);
  }

  // give the model another go — with a freshly issued URL where the caller
  // can produce one, since an expired link fails exactly like a dropped
  // connection does.
  function retryModel() {
    setFailed(false);
    if (onReload) onReload();
    else setAttempt((n) => n + 1);
  }

  // the measured mesh replaces the scanned model once it is in.
  const modelUrl = (measured && measured.modelUrl) || url;

  // the measurement runs on the model file the vision job produced, so it
  // needs that file's location — and it runs only once, so the button goes
  // away as soon as its results are in. The automatic run waits for the
  // scanned model to be on screen first, so that it does not start (and its
  // card does not show) while the model is still downloading.
  const measurable =
    !measured &&
    Boolean(metricsInput && metricsInput.dataUrl && metricsInput.dataFilename);
  const measureOnRequest = measurable && useMetrics === "enabled";
  const measureAuto =
    measurable && useMetrics === "auto" && loadedUrl === modelUrl;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        backgroundColor: BACKGROUND,
      }}
    >
      <ModelErrorBoundary
        resetKey={`${modelUrl}#${attempt}`}
        onFailed={setFailed}
      >
        <Canvas
          key={attempt}
          style={{ width: "100%", height: "100%" }}
          camera={{
            fov: 75,
            near: 0.1,
            far: 1000,
            position: [0, 0, CAMERA_DISTANCE],
          }}
        >
          <color attach="background" args={[BACKGROUND]} />
          <Suspense fallback={null}>
            <Environment files={`${process.env.PUBLIC_URL}/w.hdr`} />
            <ArcballControls ref={controlsRef} />
            <InitialView
              modelUrl={modelUrl}
              yaw={measured ? MEASURED_VIEW_YAW : 0}
              pitch={measured ? MEASURED_VIEW_PITCH : 0}
              controls={controlsRef}
              onPosed={(three) => {
                threeRef.current = three;
              }}
            />
            {/* keyed by URL: a new model is centered and scaled from scratch,
                instead of inheriting the fit of the one it replaces. */}
            <Model
              key={modelUrl}
              url={modelUrl}
              renderMode={renderMode}
              sides={shownSides}
              holes={shownHoles}
              onLoaded={setLoadedUrl}
            />
          </Suspense>
        </Canvas>
      </ModelErrorBoundary>
      {failed && (
        <ModelLoadError
          headline="Failed to load the model."
          detail="Please check the connection and try again."
          onRetry={retryModel}
        />
      )}
      <Loader
        containerStyles={{ background: "white" }}
        innerStyles={{ background: "#ddd" }}
        barStyles={{ background: "black" }}
        dataStyles={{ color: "black", fontSize: "14px" }}
      />

      {/* title, and the measured sizes once they are in, at the top center. */}
      {shownTitle && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            // keep clear of the notch/front camera when fullscreen
            pt: "max(24px, env(safe-area-inset-top))",
            px: 2,
            zIndex: 20,
          }}
        >
          <Typography
            variant="h6"
            align="center"
            sx={{
              backgroundColor: "#fff",
              borderRadius: 10,
              px: 2,
              py: 1,
              boxShadow: 2,
              textTransform: "capitalize",
            }}
          >
            {shownTitle}
          </Typography>

          {/* Measurement panel, right beneath the title. */}
          {shownSides.length > 0 && (
            <Box sx={{ width: "100%" }}>
              <MeasurementPanel sides={shownSides} />
            </Box>
          )}
        </Box>
      )}

      {/* the scan did not cover the whole foot: the cleaned mesh is shown
          without sizes, and the user is asked to look for the missing part. */}
      <Dialog
        open={incompleteOpen}
        onClose={() => setIncompleteOpen(false)}
        fullWidth
        maxWidth="xs"
        sx={{ zIndex: 1500 }}
      >
        <DialogTitle>Incomplete Scan</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Oops! Your scan is incomplete for the highlighted area. Please
            rescan slowly and cover the entire foot, following the tutorial
            video.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncompleteOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* translucent control panel with control buttons, above the model load
          error dialog. */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          px: 3,
          pt: 2.5,
          pb: "max(20px, env(safe-area-inset-bottom))",
          backgroundColor: "rgba(128,128,128,0.2)",
          zIndex: 20,
        }}
      >
        {/* Measure button for manual measurement */}
        {measureOnRequest && (
          <Box
            sx={{
              position: "absolute",
              right: 24,
              bottom: "100%",
              mb: 2,
            }}
          >
            <MeasureButton
              input={{ ...metricsInput, scanType }}
              onMeasured={showMeasured}
              onRescan={onDelete}
            />
          </Box>
        )}

        {/* the measurement running by itself — its progress card floats over
            the model, above the panel, leaving the model free to look at. */}
        {measureAuto && (
          <Box
            sx={{
              position: "absolute",
              left: 24,
              right: 24,
              bottom: "100%",
              mb: 2,
            }}
          >
            <AutoMeasureStatus
              input={{ ...metricsInput, scanType }}
              onMeasured={showMeasured}
              onRunning={setMeasuring}
              onRescan={onDelete}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
          }}
        >
          <Box sx={{ justifySelf: "start" }}>
            <RemoveButton onConfirm={onDelete} disabled={measuring} />
          </Box>
          <Box sx={{ justifySelf: "center" }}>
            <FinishButton
              onSave={
                thumbnail && !failed
                  ? () => onSave(captureThumbnail(threeRef.current))
                  : onSave
              }
              confirm={saveConfirm}
              disabled={incomplete || measuring}
            />
          </Box>
          <Box sx={{ justifySelf: "end" }}>
            <RenderModeButton
              mode={renderMode}
              onSelect={setRenderMode}
              geometryOnly={isMeshOnlyModel(modelUrl)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

ModelViewer.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string,
  scanType: PropTypes.string,
  useMetrics: PropTypes.oneOf(["disabled", "enabled", "auto"]),
  metricsInput: PropTypes.object,
  measurement: PropTypes.shape({
    complete: PropTypes.bool,
    sides: PropTypes.array,
    holes: PropTypes.array,
  }),
  onMeasured: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onReload: PropTypes.func,
  saveConfirm: PropTypes.bool,
  thumbnail: PropTypes.bool,
};

// Sets the camera's starting view of the model whenever a new model
// (`modelUrl`) or view angle comes in. The arcball controls take the view as
// their new home, so rotating starts from it.
//
// 'yaw' - how far the camera orbits to the user's right, in degrees, so that
// the model appears turned to the user's left.
// 'pitch' - how far the camera rises above the model, in degrees, to look
// down on it.
// 'onPosed' - called with the three.js state({ gl, scene, camera }) and the
// applied pose (initialPose), for the save-time thumbnail.
const InitialView = ({ modelUrl, yaw, pitch, controls, onPosed }) => {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const scene = useThree((state) => state.scene);

  useEffect(() => {
    const yawAngle = (yaw * Math.PI) / 180;
    const pitchAngle = (pitch * Math.PI) / 180;
    // the camera's distance along the ground once it is raised by the pitch.
    const ground = CAMERA_DISTANCE * Math.cos(pitchAngle);
    camera.position.set(
      ground * Math.sin(yawAngle),
      CAMERA_DISTANCE * Math.sin(pitchAngle),
      ground * Math.cos(yawAngle),
    );
    camera.up.set(0, 1, 0);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    // setCamera() re-reads the camera as the controls' initial state.
    if (controls.current) controls.current.setCamera(camera);
    onPosed({ gl, scene, camera, initialPose: cameraPose(camera) });
    // re-run only with a new model or view angle, not when a callback
    // identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelUrl, yaw, pitch, camera, gl, scene, controls]);

  return null;
};

InitialView.propTypes = {
  modelUrl: PropTypes.string.isRequired,
  yaw: PropTypes.number.isRequired,
  pitch: PropTypes.number.isRequired,
  controls: PropTypes.shape({ current: PropTypes.object }).isRequired,
  onPosed: PropTypes.func.isRequired,
};

// shared 56px round-button base
const roundButtonSx = {
  width: 56,
  height: 56,
  borderRadius: "50%",
  p: 0,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
// a round button greyed out while it is not to be used.
const disabledButtonSx = {
  opacity: 0.4,
  cursor: "default",
};

// confirms before saving (unless `confirm` is false — then saves directly).
// `disabled` greys the button out: the model on screen is not to be saved.
const FinishButton = ({ onSave, confirm, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleUpload = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave();
      // success: the parent transitions away from the viewer; close the dialog.
      setOpen(false);
    } catch (e) {
      setError(e.message || "Upload failed. Please try again.");
      // surface the error (with Retry) even when saving skipped the dialog.
      setOpen(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Finish"
        disabled={disabled}
        onClick={() => {
          setError(null);
          if (confirm) {
            setOpen(true);
          } else {
            handleUpload();
          }
        }}
        sx={{
          ...roundButtonSx,
          backgroundColor: "primary.main",
          border: "2px solid #fff",
          "&:disabled": { ...disabledButtonSx, backgroundColor: "grey.600" },
        }}
      >
        <CheckIcon size={32} color="#fff" />
      </Box>

      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        sx={{ zIndex: 1500 }}
      >
        <DialogTitle>Upload the data and go back?</DialogTitle>
        {error && (
          <DialogContent>
            <Alert severity="error">{error}</Alert>
          </DialogContent>
        )}
        <DialogActions>
          <Button
            onClick={handleUpload}
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {saving ? "Uploading…" : error ? "Retry" : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

FinishButton.propTypes = {
  onSave: PropTypes.func.isRequired,
  confirm: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
};

// confirms before discarding. `disabled` greys the button out.
const RemoveButton = ({ onConfirm, disabled = false }) => {
  const theme = useTheme();
  const red = theme.palette.error.main;
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Delete"
        disabled={disabled}
        onClick={() => setOpen(true)}
        sx={{
          ...roundButtonSx,
          backgroundColor: "transparent",
          border: `2px solid ${red}`,
          "&:disabled": disabledButtonSx,
        }}
      >
        <DeleteIcon size={32} color={red} />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} sx={{ zIndex: 1500 }}>
        <DialogTitle>Discard this data and rescan?</DialogTitle>
        <DialogActions>
          <Button
            color="error"
            onClick={() => {
              setOpen(false);
              onConfirm();
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

RemoveButton.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// opens a dropdown of render modes
const RenderModeButton = ({ mode, onSelect, geometryOnly }) => {
  const theme = useTheme();
  const green = theme.palette.primary.main;
  const [anchor, setAnchor] = useState(null);

  const iconFor = (m, size) =>
    m === "wireframe" ? (
      <WireframeIcon size={size} color={green} />
    ) : m === "textureOff" ? (
      <PaletteIcon size={size} color={green} outlined />
    ) : (
      <PaletteIcon size={size} color={green} />
    );

  // the measured mesh carries no texture — "Color On" would show nothing new.
  const items = geometryOnly
    ? [
        { value: "wireframe", label: "X-Ray View" },
        { value: "textureOff", label: "Mesh View" },
      ]
    : [
        { value: "wireframe", label: "X-Ray View" },
        { value: "textureOff", label: "Color Off" },
        { value: "textureOn", label: "Color On" },
      ];

  const choose = (value) => {
    onSelect(value);
    setAnchor(null);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Change render mode"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          ...roundButtonSx,
          backgroundColor: "transparent",
          border: `2px solid ${green}`,
        }}
      >
        {iconFor(mode, 32)}
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        slotProps={{ paper: { sx: { width: 240 } } }}
        sx={{ zIndex: 1500 }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.value}
            selected={mode === item.value}
            onClick={() => choose(item.value)}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {mode === item.value && <CheckIcon size={18} color={green} />}
            </ListItemIcon>
            <Box sx={{ mr: 1.5, display: "flex" }}>
              {iconFor(item.value, 24)}
            </Box>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

RenderModeButton.propTypes = {
  mode: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  geometryOnly: PropTypes.bool,
};
